import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_CONST } from "../../../const";
import {
  Table,
  Button,
  Input,
  Select,
  Tag,
  message,
  Dropdown,
  Modal,
  Descriptions,
  Spin,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  EditOutlined,
  MoreOutlined,
  FileTextOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  PageHeader,
  FinanceContainer,
  MetricsCard,
  MetricsGrid,
  FiltersContainer,
  TableContainer,
  Flex,
} from "./FinanceManagement.style";

const { Option } = Select;

const formatLCurrency = (amount) => {
  if (amount >= 10000000) {
    return `${(amount / 10000000).toFixed(2)}Cr`;
  } else if (amount >= 100000) {
    return `${(amount / 100000).toFixed(2)}L`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}K`;
  }
  return amount.toLocaleString("en-IN");
};

const FinanceManagement = () => {
  const [searchText, setSearchText] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Modal states
  const [modifyModalVisible, setModifyModalVisible] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [modifyReason, setModifyReason] = useState("");
  const [updatePayableModalVisible, setUpdatePayableModalVisible] = useState(false);
  const [selectedPayableStatus, setSelectedPayableStatus] = useState("");

  // Customer invoices from API
  const [payables, setPayables] = useState([]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }
      if (filterStatus) body.customerStatus = filterStatus.toLowerCase();
      
      const response = await axios.post(API_CONST.GET_INVOICES, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.invoices) {
        const transformed = response.data.invoices.map(inv => ({
          id: inv.id,
          recordId: inv.id,
          customerInvoiceId: inv.customerInvoiceNumber,
          talentId: inv.talentId,
          talentName: inv.talentName || 'Unknown',
          jobId: inv.jobId,
          month: inv.monthName,
          year: String(inv.year),
          days: inv.billableDays != null ? inv.billableDays : (inv.billableHours ? Math.round(inv.billableHours / 8) : 0),
          hours: inv.billableHours || 0,
          amount: parseFloat(inv.customerSubtotal) || 0,
          gst: parseFloat(inv.customerTaxAmount) || 0,
          totalAmount: parseFloat(inv.customerTotalAmount) || 0,
          status: inv.customerStatus ? inv.customerStatus.charAt(0).toUpperCase() + inv.customerStatus.slice(1) : 'Draft',
          timesheetStatus: inv.timesheetStatus ? inv.timesheetStatus.charAt(0).toUpperCase() + inv.timesheetStatus.slice(1) : 'Pending',
          dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString().split('T')[0] : '',
          paidOn: inv.customerPaidAt ? new Date(inv.customerPaidAt).toISOString().split('T')[0] : '',
        }));
        setPayables(transformed);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterStatus]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("authToken");
      const now = new Date();
      await axios.post(API_CONST.SYNC_NOW, {
        month: now.getMonth() + 1,
        year: now.getFullYear()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success("Sync completed successfully");
      fetchInvoices();
    } catch (error) {
      console.error("Error syncing:", error);
      message.error(error.response?.data?.message || "Failed to sync");
    } finally {
      setSyncing(false);
    }
  };

  // Action handlers
  const handleViewPayable = (record) => {
    Modal.info({
      title: `Invoice Details - ${record.customerInvoiceId || 'N/A'}`,
      width: 600,
      content: (
        <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }}>
          <Descriptions.Item label="Record ID">{record.recordId?.substring(0, 12)}...</Descriptions.Item>
          <Descriptions.Item label="Invoice ID">{record.customerInvoiceId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Talent Name">{record.talentName}</Descriptions.Item>
          <Descriptions.Item label="Job ID">{record.jobId}</Descriptions.Item>
          <Descriptions.Item label="Month">
            {record.month} {record.year}
          </Descriptions.Item>
          <Descriptions.Item label="Days">{record.days}</Descriptions.Item>
          <Descriptions.Item label="Hours">{record.hours}</Descriptions.Item>
          <Descriptions.Item label="Amount">
            ₹ {(record.amount || 0).toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="GST (18%)">
            ₹ {(record.gst || 0).toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            <strong style={{ fontSize: 16, color: "#014c75" }}>
              ₹ {(record.totalAmount || 0).toLocaleString("en-IN")}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Due Date">{record.dueDate || '-'}</Descriptions.Item>
          {record.paidOn && (
            <Descriptions.Item label="Paid On">{record.paidOn}</Descriptions.Item>
          )}
          <Descriptions.Item label="Status">
            <Tag
              color={
                record.status === "Paid" || record.status === "Approved"
                  ? "green"
                  : record.status === "Sent"
                  ? "blue"
                  : record.status === "Overdue"
                  ? "red"
                  : "gold"
              }
            >
              {record.status}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      ),
    });
  };

  const handleDownloadInvoice = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.INVOICE_DOWNLOAD_URL}/${record.id}?invoiceType=customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        message.warning("Invoice file not available");
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      message.error(error.response?.data?.message || "Failed to download invoice");
    }
  };

  const handleDownloadTimesheet = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_TIMESHEET_DOWNLOAD_URL,
        { invoiceId: record.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        message.warning("Timesheet file not available");
      }
    } catch (error) {
      console.error("Error downloading timesheet:", error);
      message.error(error.response?.data?.message || "Failed to download timesheet");
    }
  };

  const handleApprove = (record) => {
    Modal.confirm({
      title: "Mark Invoice as Paid",
      content: `Are you sure you want to mark invoice ${record.customerInvoiceId || 'this invoice'} as paid for ${record.talentName}?`,
      okText: "Mark as Paid",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(`${API_CONST.UPDATE_INVOICE_STATUS}/${record.id}`, {
            statusType: 'customer',
            status: 'paid'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const updated = payables.map((p) =>
            p.id === record.id
              ? { ...p, status: "Paid", paidOn: new Date().toISOString().split("T")[0] }
              : p
          );
          setPayables(updated);
          
          if (selectedPayable && selectedPayable.id === record.id) {
            setSelectedPayable(null);
          }
          
          message.success(`Invoice ${record.customerInvoiceId || record.id} marked as paid`);
        } catch (error) {
          console.error("Error updating invoice status:", error);
          message.error(error.response?.data?.message || "Failed to update invoice status");
        }
      },
    });
  };

  const handleApproveInvoice = (record) => {
    Modal.confirm({
      title: "Approve Invoice",
      content: `Approve invoice ${record.customerInvoiceId || record.id} for ${record.talentName}? This confirms the invoice is correct and authorises payment processing.`,
      okText: "Approve",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(`${API_CONST.UPDATE_INVOICE_STATUS}/${record.id}`, {
            statusType: 'customer',
            status: 'approved'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const updated = payables.map((p) =>
            p.id === record.id ? { ...p, status: "Approved" } : p
          );
          setPayables(updated);
          message.success(`Invoice ${record.customerInvoiceId || record.id} approved successfully`);
        } catch (error) {
          console.error("Error approving invoice:", error);
          message.error(error.response?.data?.message || "Failed to approve invoice");
        }
      },
    });
  };

  const handleAskToModify = (record) => {
    setSelectedPayable(record);
    setModifyReason("");
    setModifyModalVisible(true);
  };

  const handleSubmitModifyRequest = async () => {
    if (!modifyReason.trim()) {
      message.warning("Please enter a reason for modification");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.REQUEST_INVOICE_MODIFICATION}/${selectedPayable.id}/request-modification`,
        { reason: modifyReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`Modification request sent for invoice ${selectedPayable.customerInvoiceId || selectedPayable.id}. Admin will review your request.`);
      setModifyModalVisible(false);
      setModifyReason("");
      setSelectedPayable(null);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to send modification request");
    }
  };

  const handleCancelModifyModal = () => {
    setModifyModalVisible(false);
    setModifyReason("");
    setSelectedPayable(null);
  };

  const handleUpdatePayable = (record) => {
    setSelectedPayable(record);
    setSelectedPayableStatus(record.status === "Paid" ? "Paid" : "Sent");
    setUpdatePayableModalVisible(true);
  };

  const handleSubmitUpdatePayable = async () => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(`${API_CONST.UPDATE_INVOICE_STATUS}/${selectedPayable.id}`, {
        statusType: 'customer',
        status: selectedPayableStatus.toLowerCase()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updated = payables.map((p) => {
        if (p.id === selectedPayable.id) {
          if (selectedPayableStatus === "Paid") {
            return { 
              ...p, 
              status: "Paid",
              paidOn: p.paidOn || new Date().toISOString().split("T")[0]
            };
          } else {
            return { 
              ...p, 
              status: selectedPayableStatus
            };
          }
        }
        return p;
      });
      setPayables(updated);
      message.success(`Invoice ${selectedPayable.customerInvoiceId || selectedPayable.id} status updated to ${selectedPayableStatus}`);
      setUpdatePayableModalVisible(false);
      setSelectedPayable(null);
      setSelectedPayableStatus("");
    } catch (error) {
      console.error("Error updating invoice status:", error);
      message.error(error.response?.data?.message || "Failed to update invoice status");
    }
  };

  const handleCancelUpdatePayable = () => {
    setUpdatePayableModalVisible(false);
    setSelectedPayable(null);
    setSelectedPayableStatus("");
  };

  // Filter data
  const getFilteredData = () => {
    let filtered = [...payables];

    if (searchText) {
      filtered = filtered.filter(
        (p) =>
          (p.talentName || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (p.customerInvoiceId || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (p.recordId || '').toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filterMonth) filtered = filtered.filter((p) => p.month === filterMonth);
    if (filterStatus) filtered = filtered.filter((p) => p.status === filterStatus);

    return filtered;
  };

  // Get unique values for filters
  const months = [...new Set(payables.map((p) => p.month).filter(Boolean))];
  const statuses = [...new Set(payables.map((p) => p.status).filter(Boolean))];

  // Calculate metrics based on new customer status values (draft/sent/paid/overdue)
  const getMetrics = () => {
    const totalPaid = payables
      .filter((p) => p.status === "Paid")
      .reduce((sum, p) => sum + (p.totalAmount || 0), 0);
    const currentPayable = payables
      .filter((p) => p.status === "Draft" || p.status === "Sent" || p.status === "Approved")
      .reduce((sum, p) => sum + (p.totalAmount || 0), 0);
    const pendingApproval = payables.filter((p) => p.status === "Sent").length;
    const totalInvoices = payables.length;

    return { totalPaid, currentPayable, pendingApproval, totalInvoices };
  };

  const metrics = getMetrics();

  // Define action menu items based on status
  // Note: Customers can view invoices but cannot update status (Admin manages that via Reconciliation)
  const getActionMenuItems = (record) => {
    const items = [
      {
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () => handleViewPayable(record),
      },
      {
        key: "download-invoice",
        label: "Download Invoice",
        icon: <DownloadOutlined />,
        onClick: () => handleDownloadInvoice(record),
      },
      {
        key: "download-timesheet",
        label: "Download Timesheet",
        icon: <FileTextOutlined />,
        onClick: () => handleDownloadTimesheet(record),
      },
    ];

    if (record.status === "Sent") {
      items.push({
        key: "approve",
        label: "Approve Invoice",
        icon: <CheckCircleOutlined />,
        onClick: () => handleApproveInvoice(record),
      });
      items.push({
        key: "modify",
        label: "Request Modification",
        icon: <EditOutlined />,
        onClick: () => handleAskToModify(record),
      });
    }

    return items;
  };

  const columns = [
    {
      title: "Record ID",
      dataIndex: "recordId",
      key: "recordId",
      width: 110,
      ellipsis: true,
      render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{text?.substring(0, 12)}...</span>,
    },
    {
      title: "Invoice ID",
      dataIndex: "customerInvoiceId",
      key: "customerInvoiceId",
      width: 150,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#52c41a', fontWeight: 500 }}>{text || '-'}</span>,
    },
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 160,
    },
    {
      title: "Month",
      key: "month",
      width: 90,
      render: (_, record) => `${record.month?.substring(0, 3)} '${record.year?.toString().substring(2)}`,
    },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 70,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      width: 70,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      align: /** @type {'right'} */ ("right"),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#595959" }}>
          ₹ {(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "GST (18%)",
      dataIndex: "gst",
      key: "gst",
      width: 110,
      align: /** @type {'right'} */ ("right"),
      render: (gst) => (
        <span style={{ fontWeight: 500, color: "#8c8c8c" }}>
          ₹ {(gst || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 140,
      align: /** @type {'right'} */ ("right"),
      render: (amount) => (
        <strong style={{ color: "#014c75", fontSize: 14 }}>
          ₹ {(amount || 0).toLocaleString("en-IN")}
        </strong>
      ),
    },
    {
      title: "Timesheet",
      dataIndex: "timesheetStatus",
      key: "timesheetStatus",
      width: 110,
      align: /** @type {'center'} */ ("center"),
      render: (status) => (
        <Tag color={status === "Approved" ? "green" : status === "Verified" ? "blue" : "orange"}>{status}</Tag>
      ),
    },
    {
      title: "Invoice",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => {
        let color = "gold";
        if (status === "Paid" || status === "Approved") color = "green";
        else if (status === "Sent") color = "blue";
        else if (status === "Overdue") color = "red";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      width: 110,
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      fixed: /** @type {'right'} */ ("right"),
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const renderFilters = () => (
    <FiltersContainer>
      <Flex style={{ gap: "12px", flexWrap: "wrap" }}>
        <Input
          placeholder="Search by Talent Name, Invoice ID, or Job ID"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Select
          placeholder="Select Month"
          value={filterMonth}
          onChange={setFilterMonth}
          style={{ width: 150 }}
          allowClear
        >
          {months.map((m) => (
            <Option key={m} value={m}>
              {m}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Select Status"
          value={filterStatus}
          onChange={setFilterStatus}
          style={{ width: 150 }}
          allowClear
        >
          {statuses.map((s) => (
            <Option key={s} value={s}>
              {s}
            </Option>
          ))}
        </Select>
      </Flex>
    </FiltersContainer>
  );

  return (
    <FinanceContainer>
      <PageHeader>
        <h1>Invoice</h1>
        <p>View and manage invoices for your engagements</p>
      </PageHeader>

      {/* Metrics */}
      <MetricsGrid>
        <MetricsCard>
          <div className="metric-label">Total Amount Paid</div>
          <div className="metric-value">₹ {formatLCurrency(metrics.totalPaid)}</div>
        </MetricsCard>
        <MetricsCard>
          <div className="metric-label">Current Payable</div>
          <div className="metric-value">₹ {formatLCurrency(metrics.currentPayable)}</div>
        </MetricsCard>
        <MetricsCard>
          <div className="metric-label">Pending Invoices</div>
          <div className="metric-value">{metrics.pendingApproval}</div>
        </MetricsCard>
        <MetricsCard>
          <div className="metric-label">Total Invoices</div>
          <div className="metric-value">{metrics.totalInvoices}</div>
        </MetricsCard>
      </MetricsGrid>

      {/* Filters */}
      {renderFilters()}

      {/* Table */}
      <TableContainer>
        <Table
          columns={columns}
          dataSource={getFilteredData()}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            pageSize: 10,
            showTotal: (total) => `Total ${total} invoices`,
          }}
          scroll={{ x: 1600 }}
        />
      </TableContainer>

      {/* Ask to Modify Modal */}
      <Modal
        title={`Request Modification - ${selectedPayable?.customerInvoiceId || selectedPayable?.id || ""}`}
        open={modifyModalVisible}
        onOk={handleSubmitModifyRequest}
        onCancel={handleCancelModifyModal}
        width={600}
        okText="Send Request"
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        {selectedPayable && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
              <div><strong>Invoice ID:</strong> {selectedPayable.customerInvoiceId || '-'}</div>
              <div><strong>Talent Name:</strong> {selectedPayable.talentName}</div>
              <div><strong>Month:</strong> {selectedPayable.month} {selectedPayable.year}</div>
              <div><strong>Total Amount:</strong> ₹ {(selectedPayable.totalAmount || 0).toLocaleString("en-IN")}</div>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Reason for Modification *
              </label>
              <Input.TextArea
                rows={4}
                placeholder="Please specify what needs to be modified in the invoice..."
                value={modifyReason}
                onChange={(e) => setModifyReason(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Update Payable Modal */}
      <Modal
        title={`Update Payable Status - ${selectedPayable?.customerInvoiceId || selectedPayable?.id || ""}`}
        open={updatePayableModalVisible}
        onOk={handleSubmitUpdatePayable}
        onCancel={handleCancelUpdatePayable}
        width={600}
        okText="Update Status"
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        {selectedPayable && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
              <div><strong>Invoice ID:</strong> {selectedPayable.customerInvoiceId || '-'}</div>
              <div><strong>Talent Name:</strong> {selectedPayable.talentName}</div>
              <div><strong>Month:</strong> {selectedPayable.month} {selectedPayable.year}</div>
              <div><strong>Total Amount:</strong> ₹ {(selectedPayable.totalAmount || 0).toLocaleString("en-IN")}</div>
              <div><strong>Current Status:</strong> <Tag color={selectedPayable.status === "Paid" ? "green" : selectedPayable.status === "Sent" ? "blue" : selectedPayable.status === "Overdue" ? "red" : "gold"}>{selectedPayable.status}</Tag></div>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Update Status *
              </label>
              <Select
                value={selectedPayableStatus}
                onChange={setSelectedPayableStatus}
                style={{ width: "100%" }}
              >
                <Option value="Paid">Paid</Option>
                <Option value="Sent">Sent</Option>
              </Select>
            </div>
          </div>
        )}
      </Modal>
    </FinanceContainer>
  );
};

export default FinanceManagement;
